import { useState } from 'react';
import { Box, Button, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DECISION_META, HV_DECISIONS, type ApplicantDetail, type HouseholdDetail, type HvDecision } from '@hv/shared';
import { useUpdateApplicant } from '../api/hooks';
import { citeAnchor } from '../assistant/citations';
import { useAuth } from '../components/AuthContext';
import { DecisionPill } from '../components/badges';
import { useNotify } from '../components/Notify';
import { BRAND } from '../theme';
import { Section } from './Section';

function veracrossDetails(a: ApplicantDetail): string {
  return [
    a.campusApplying,
    a.admissionSource && `Source: ${a.admissionSource}`,
    a.inquiryDate && `Inquired ${a.inquiryDate}`,
    a.applicationDate && `Applied ${a.applicationDate}`,
    a.appliedPreviously && `Previously applied: ${a.appliedPreviously}`,
    a.enrollmentDecision && `Enrollment: ${a.enrollmentDecision}${a.enrollmentDecisionDate ? ` (${a.enrollmentDecisionDate})` : ''}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

function ApplicantRow({ a }: { a: ApplicantDetail }) {
  const { can } = useAuth();
  const { notify, notifyError } = useNotify();
  const update = useUpdateApplicant();
  const [likelihood, setLikelihood] = useState(a.enrollmentLikelihood ?? '');
  const editable = can('decisions.write');
  const details = veracrossDetails(a);

  const changeDecision = (hvDecision: HvDecision) =>
    update.mutate(
      { id: a.id, hvDecision },
      {
        onSuccess: () => notify('success', `${a.firstName}: decision set to ${DECISION_META[hvDecision].label}`),
        onError: (err) => notifyError(err, 'Changing decision failed'),
      },
    );

  return (
    <Box sx={{ p: 1.5, border: `1px solid ${BRAND.line}`, borderRadius: 2 }} data-testid={`applicant-${a.firstName}`} {...citeAnchor(`applicant:${a.id}`)}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
        <Box>
          <Typography fontWeight={700}>
            {a.firstName}
            {a.gradeApplying ? ` · Grade ${a.gradeApplying}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {a.admissionStatus ? `Veracross status: ${a.admissionStatus}` : 'Not yet in Veracross'}
            {a.checklistTotal ? ` · checklist ${a.checklistCompleted ?? 0}/${a.checklistTotal}` : ''}
          </Typography>
          {details && (
            <Typography variant="caption" color="text.secondary" display="block" data-testid={`applicant-details-${a.firstName}`}>
              {details}
            </Typography>
          )}
        </Box>
        {a.veracrossUrl && (
          <Link href={a.veracrossUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13 }}>
            View in Veracross <OpenInNewIcon fontSize="inherit" />
          </Link>
        )}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} mt={1.5} alignItems={{ sm: 'center' }}>
        {editable ? (
          <TextField
            select
            size="small"
            label={`Decision for ${a.firstName}`}
            value={a.hvDecision}
            onChange={(e) => changeDecision(e.target.value as HvDecision)}
            disabled={update.isPending}
            sx={{ minWidth: 220 }}
          >
            {HV_DECISIONS.map((d) => (
              <MenuItem key={d} value={d}>
                <DecisionPill decision={d} />
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <DecisionPill decision={a.hvDecision} />
        )}
      </Stack>
      <Stack direction="row" gap={1} mt={1.5} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          label="Enrollment likelihood"
          value={likelihood}
          onChange={(e) => setLikelihood(e.target.value)}
          disabled={!editable}
          multiline
          maxRows={4}
        />
        {editable && (
          <Button
            disabled={likelihood === (a.enrollmentLikelihood ?? '') || update.isPending}
            onClick={() =>
              update.mutate(
                { id: a.id, enrollmentLikelihood: likelihood.trim() || null },
                { onSuccess: () => notify('success', 'Enrollment likelihood saved'), onError: (err) => notifyError(err, 'Saving enrollment likelihood failed') },
              )
            }
          >
            Save
          </Button>
        )}
      </Stack>
    </Box>
  );
}

export function ApplicantsSection({ h }: { h: HouseholdDetail }) {
  return (
    <Section title="Applicants & Decisions">
      {h.applicants.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No applicants recorded for this household yet.
        </Typography>
      )}
      <Stack gap={1.5}>
        {h.applicants.map((a) => (
          <ApplicantRow key={`${a.id}-${a.enrollmentLikelihood ?? ''}`} a={a} />
        ))}
      </Stack>
    </Section>
  );
}
